import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import type { EFTMapImgs } from "@/data-models/eft-weapon-maps-images.mjs";
import { GAME_SYMBOL_EFT } from "@/game-eft/definition-symbol.mjs";
import type { FleaMarketData } from "@/game-eft/models/flea-market.mjs";
import type {
  Items,
  LeaderboardsMap,
  LeaderboardsPlayer,
  LeaderboardsWeapon,
  Match,
  Matchlist,
  PlayerFactionStats,
  PlayerMapStats,
  PlayerSeasonStat,
  PlayerWeaponStats,
  Profile,
  Seasons,
} from "@/game-eft/models/graphql.mjs";
import type {
  WikiCaliber,
  WikiCategory,
  WikiItem,
  WikiNode,
  WikiTrader,
} from "@/game-eft/models/model-wiki.mjs";
import type { ItemsLocale, MapMarkers } from "@/game-eft/models/static.mjs";
import routes from "@/game-eft/routes.mjs";
import type { LiveGame } from "@/game-eft/utils/actions.mjs";
import GameIconBadgeEFT from "@/inline-assets/game-icon-badge-eft.svg";
import GameIconBoxEFT from "@/inline-assets/game-icon-box-eft.svg";

export { GAME_SYMBOL_EFT };

const EFT_OVERLAY_SETTINGS = {
  enableInventoryOverlay: true,
  useLow24hPrice: false,
};

export type EFTOverlaySettings = typeof EFT_OVERLAY_SETTINGS;

const definition = {
  gameSymbol: GAME_SYMBOL_EFT,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/eft.svg`,
  iconShape: GameIconBoxEFT,
  iconBox: GameIconBoxEFT,
  iconBadge: GameIconBadgeEFT,
  imageHero: `${appURLs.CDN}/blitz/ui/img/game-cover/hero-eft.webp`,
  imageBanner: `${appURLs.CDN}/blitz/ui/img/game-cover/banner-eft.webp`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/eft-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/game-cover/wordmark-eft.webp`,
  isActive: true,
  fullName: ["common:games.eft.long", "Escape From Tarkov"],
  friendlyName: ["common:games.eft.short", "EFT"],
  preferFriendlyName: false as const,
  shortName: "eft" as const,
  backendName: "TARKOV" as const,
  gameColor: "#B59C6D",
  navLinks: [
    {
      href: "/eft/stats",
      text: ["common:navigation.statistics", "Statistics"],
    },
    {
      href: "/eft/leaderboards",
      text: ["common:navigation.leaderboards", "Leaderboards"],
    },
    {
      href: "/eft/maps",
      text: ["common:maps", "Maps"],
    },
    {
      href: "/eft/flea-market",
      text: ["common:navigation.fleaMarket", "Flea Market"],
    },
    {
      href: "/eft/database",
      text: ["common:navigation.database", "Database"],
    },
    {
      href: "/overlays/eft",
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
  ],
  settings: () => import("@/game-eft/Settings.jsx"),
  routes,
  meta: {
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/eft-c96bb.webp`,
    guides: [
      () => import("@/game-eft/components/HomeStatisticsTile.jsx"),
      () => import("@/game-eft/components/HomeMarketplaceTile.jsx"),
    ],
    articles: [
      {
        createdAt: new Date("Mar 7, 2024 18:00:00"),
        title: ["welcome:eft.patchNote", "Patch Notes 0.14.11"],
        subtitle: [
          "welcome:eft.patchNoteDesc",
          "Check out the latest changes to your favorite extraction shooter EFT from Patch 0.14.11!",
        ],
        link: {
          href: "/eft/patchnotes/latest",
          text: ["welcome:eft.patchNote.cta", "View Patch Notes"],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/eft/article-patchnotes.png`,
      },
      {
        createdAt: new Date("Mar 7, 2024 18:00:00"),
        title: ["welcome:eft.inventoryOverlayTitle", "Inventory Value"],
        subtitle: [
          "welcome:eft.inventoryOverlaySubtitle",
          "Enhance your Escape from Tarkov experience and discover one of a kind overlays that offer live stats, in Real-Time powered by Blitz.gg",
        ],
        link: {
          href: "/overlays/eft",
          text: [
            "common:navigation.viewInventoryOverlay",
            "View Inventory Value Overlay",
          ],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/eft/article-inventory.png`,
      },
    ],
    features: [
      {
        title: [
          "eft.features.dynamicStatsOverlay.title",
          "Dynamic Stats Overlay",
        ],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/eft/feature-dyn-overlay.png`,
      },
      {
        title: ["eft.features.profile.title", "Profile Page"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/eft/feature-profile.png`,
      },
      {
        title: ["eft.features.postMatchInsights.title", "Post Match Insights"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/eft/feature-postmatch.png`,
      },
      {
        title: ["common:navigation.fleaMarket", "Flea Market"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/eft/flea-market.webp`,
      },
    ],
    overlays: {
      title: ["overlays:meta.eft.title", "EFT Overlays"],
      description: [
        "overlays:meta.eft.description",
        "View Trader and Flea Market pricing for all items in Real-Time",
      ],
      subtitle: [
        "overlays:meta.eft.subtitle",
        "Enhance your Escape from Tarkov experience and discover one of a kind overlays that offer live stats, in Real-Time powered by Blitz.gg",
      ],
    },
  },
  IPC: () => import("@/game-eft/init.mjs"),
  overlays: {
    inventoryValue: {
      key: ["enableInventoryOverlay"],
      title: ["welcome:eft.inventoryOverlayTitle", "Inventory Value"],
      tags: ["utility"],
      description: [
        "welcome:eft.inventoryOverlaySubtitle",
        "View Trader and Flea Market pricing for all items in Real-Time",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/eft/1000x880.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/eft/840x486.webp`,
        featured: `${appURLs.CDN}/blitz/ui/img/settings/eft/1016x1240.webp`,
      },
      native: true,
      date: new Date(500),
      features: [
        {
          text: ["overlays:eft.displayOptions.title", "Display Options"],
          items: [
            {
              title: [
                "overlays:eft.displayOptions.subtitle",
                "Customize which Flea Market prices are displayed",
              ],
              select: {
                key: ["useLow24hPrice"],
                style: "radio",
                options: [
                  {
                    value: false,
                    text: [
                      "overlays:eft.displayOptions.useAvg24hPrice",
                      "Average Price in past 24h",
                    ],
                  },
                  {
                    value: true,
                    text: [
                      "overlays:eft.displayOptions.useLow24hPrice",
                      "Lowest Price in past 24h",
                    ],
                  },
                ],
              },
            },
          ],
        },
      ],
    },
  },
  overlaySettingsKey: "eftOverlaySettings",
  overlaySettings: EFT_OVERLAY_SETTINGS,
} satisfies GameDefinition<typeof GAME_SYMBOL_EFT>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    eft: {
      fleaMarket: FleaMarketData;
      imgs: {
        maps: EFTMapImgs;
        weapons: Record<string, string>;
      };
      items: Items;
      itemsLocale: ItemsLocale;
      leaderboards: {
        arena: unknown;
        maps: LeaderboardsMap[];
        players: Record<string, Record<string, LeaderboardsPlayer[]>>;
        weapons: LeaderboardsWeapon[];
      };
      liveGame: LiveGame | null;
      maps: {
        markers: Record<string, Record<string, MapMarkers>>;
      };
      matches: Record<string, Match | Error>;
      matchlists: Matchlist;
      matchtiles: unknown;
      meta: {
        currentSeason: unknown;
        maps: Record<string, unknown>;
        seasons: Record<string, unknown>;
        items: Record<string, unknown>;
      };
      playerFactionStat: Record<string, PlayerFactionStats>;
      playerMapStat: Record<string, PlayerMapStats>;
      playerSeasonStat: Record<string, PlayerSeasonStat>;
      playerWeaponStat: Record<string, Record<string, PlayerWeaponStats>>;
      profiles: Record<string, Profile>;
      seasons: Seasons;
      weaponHits: unknown;
      weaponKills: unknown;
      wiki: {
        calibers: Record<string, WikiCaliber>;
        categories: Record<string, WikiCategory>;
        i18n: Record<string, string>;
        items: Record<string, WikiItem>;
        nodes: Record<string, WikiNode>;
        traders: Record<string, WikiTrader>;
        version: [string];
      };
    };
  }
}

export default definition;
